import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getRouteMutationKey } from '.';
import { routeService } from '../../../../application/RouteService';
import * as routing from '../../../../constants/Routing';
import { Route } from '../../../../domain/route/Route';
import { Stop } from '../../../../domain/stops/Stop';
import { useErrorMiddleware } from '../../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';
import { RouteActionType } from '../context/actions';
import { useRouteContext } from '../context/context';

interface UseCreateStopsProps {
  route: Route;
}

interface UseCreateStopsMutationProps {
  stops: Stop[];
  optimize: boolean;
}

type UseCreateStopsResponseProps = ServiceResponse<({ stops, optimize }: UseCreateStopsMutationProps) => void, Route>;

// Hooks act as 'Adapter' layer.
export const useCreateStops = ({ route }: UseCreateStopsProps): UseCreateStopsResponseProps => {
  const queryClient = useQueryClient();
  const { routeState, routeDispatch } = useRouteContext();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const navigate = useNavigate();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isError, isSuccess, mutate } = useMutation<Route, unknown, UseCreateStopsMutationProps>(
    ({ stops, optimize }: UseCreateStopsMutationProps) => routeService.createStops({ stops, route, optimize }),
    {
      onSuccess: (newRoute) => {
        const key = getRouteMutationKey({ routeId: route.id });
        let previousQueryContent = queryClient.getQueryData<Route>(key);

        if (!previousQueryContent) {
          throw new Error(`previousQueryContent does not exist`);
        }

        previousQueryContent = newRoute;

        queryClient.setQueryData<Route>(key, previousQueryContent);
        routeDispatch({ type: RouteActionType.FETCH_ROUTE_SUCCESS, payload: newRoute });
      },
      onError: (err: any) => {
        console.info(`Create Stops Error:`, err);
        // If it fails, the stops are probably created.
        // We need to invalidate the query to refetch the route.
        queryClient.invalidateQueries(getRouteMutationKey({ routeId: route.id }));

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
        navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, route.id));
        return;
      },
    }
  );

  return {
    run: mutate,
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    data: routeState.route,
  };
};
